import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Send, Sparkles } from 'lucide-react';

/**
 * AssistProfilesCarousel — carrossel demonstrativo dos 5 VUKZ Assists.
 * 100% estático: zero API, zero auth, zero token. O campo de mensagem e os
 * chips são apenas visuais. Mesma linguagem de interação do ProfilesCarousel.
 */
const V = { text: '#111111', textSoft: '#555555', border: '#e6e6e6', cardBg: '#fafafa', tagBg: '#f3f3f3' };

const AssistChatPreview = ({ profile, inputPlaceholder }) => (
  <div className="rounded-2xl border overflow-hidden bg-white" style={{ borderColor: V.border }}>
    {/* Header do chat */}
    <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: V.border, backgroundColor: V.cardBg }}>
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: V.text }}
        aria-hidden="true"
      >
        <Sparkles size={16} color="#ffffff" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-bold truncate" style={{ color: V.text }}>{profile.assistantName}</div>
        <div className="text-xs flex items-center gap-1.5" style={{ color: V.textSoft }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#16a34a' }} aria-hidden="true" />
          VUKZ Assist
        </div>
      </div>
    </div>

    {/* Corpo do chat */}
    <div className="px-4 py-5" style={{ minHeight: 220 }}>
      <div className="flex items-start gap-2.5 max-w-[92%]">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          style={{ backgroundColor: V.tagBg }}
          aria-hidden="true"
        >
          <Sparkles size={13} style={{ color: V.text }} />
        </div>
        <div
          className="rounded-2xl rounded-tl-md px-4 py-3 text-sm leading-relaxed"
          style={{ backgroundColor: V.cardBg, color: V.text, border: `1px solid ${V.border}` }}
        >
          {profile.greeting}
        </div>
      </div>

      {/* Chips de sugestão (visuais) */}
      <div className="mt-4 ml-9 flex flex-wrap gap-2">
        {profile.suggestions.map((s) => (
          <span
            key={s}
            className="px-3 py-1.5 rounded-full text-xs font-medium border"
            style={{ borderColor: V.border, color: V.textSoft, backgroundColor: '#ffffff' }}
          >
            {s}
          </span>
        ))}
      </div>
    </div>

    {/* Campo de entrada (demonstrativo) */}
    <div className="px-4 py-3 border-t flex items-center gap-2" style={{ borderColor: V.border }}>
      <div
        className="flex-1 px-4 py-2.5 rounded-xl text-sm border"
        style={{ borderColor: V.border, color: '#9ca3af', backgroundColor: V.cardBg }}
        aria-hidden="true"
      >
        {inputPlaceholder}
      </div>
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: V.text }}
        aria-hidden="true"
      >
        <Send size={16} color="#ffffff" />
      </div>
    </div>
  </div>
);

const AssistProfilesCarousel = ({ title, subtitle, profiles, ariaLabel, inputPlaceholder, prevLabel, nextLabel }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const reducedMotion = useMemo(
    () => typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    []
  );

  const go = useCallback(
    (delta) => setIndex((i) => (i + delta + profiles.length) % profiles.length),
    [profiles.length]
  );

  useEffect(() => {
    if (paused || reducedMotion) return undefined;
    const t = setInterval(() => go(1), 6000);
    return () => clearInterval(t);
  }, [paused, reducedMotion, go]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
    if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
  };
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 48) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <section id="assist" className="py-20" style={{ backgroundColor: V.cardBg }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div className="text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide uppercase mb-6"
            style={{ backgroundColor: V.tagBg, color: V.textSoft }}
          >
            <Sparkles size={14} aria-hidden="true" />
            VUKZ Assist
          </div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight" style={{ color: V.text }}>{title}</h2>
          <p className="mt-3 text-base sm:text-lg max-w-2xl mx-auto" style={{ color: V.textSoft }}>{subtitle}</p>
        </div>

        <div
          role="region"
          aria-roledescription="carousel"
          aria-label={ariaLabel}
          tabIndex={0}
          onKeyDown={onKeyDown}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          className="mt-10 outline-none relative"
        >
          {profiles.map((p, i) => (
            <div
              key={p.role}
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} / ${profiles.length}: ${p.name}`}
              aria-hidden={i !== index}
              style={{ display: i === index ? 'block' : 'none' }}
            >
              <div className="text-center mb-4">
                <span
                  className="inline-block px-4 py-1.5 rounded-full text-sm font-bold border bg-white"
                  style={{ borderColor: V.border, color: V.text }}
                >
                  {p.name}
                </span>
              </div>
              <AssistChatPreview profile={p} inputPlaceholder={inputPlaceholder} />
            </div>
          ))}

          <button
            type="button"
            onClick={() => go(-1)}
            aria-label={prevLabel}
            className="absolute -left-2 sm:-left-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border shadow-sm flex items-center justify-center hover:bg-gray-50"
            style={{ borderColor: V.border, color: V.text }}
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label={nextLabel}
            className="absolute -right-2 sm:-right-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border shadow-sm flex items-center justify-center hover:bg-gray-50"
            style={{ borderColor: V.border, color: V.text }}
          >
            <ChevronRight size={20} />
          </button>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2" role="tablist" aria-label={ariaLabel}>
          {profiles.map((p, i) => (
            <button
              key={p.role}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={p.name}
              onClick={() => setIndex(i)}
              className="rounded-full transition-all"
              style={{
                width: i === index ? 24 : 8,
                height: 8,
                backgroundColor: i === index ? V.text : '#d1d5db',
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export const ASSIST_PROFILES_PT = [
  {
    role: 'athlete',
    name: 'Atleta',
    assistantName: 'VUKZ Assist — Atleta',
    greeting: 'Olá, Lucas. Estou aqui para ajudar você a organizar seu dia esportivo. Posso explicar seu treino, acompanhar seu bem-estar, consultar sua agenda e ajudar você a entender seu plano alimentar.',
    suggestions: ['Qual é meu treino de hoje?', 'Como está minha recuperação?', 'Qual é minha próxima refeição?'],
  },
  {
    role: 'coach',
    name: 'Treinador',
    assistantName: 'VUKZ Assist — Treinador',
    greeting: 'Olá, Ana. Posso ajudar você a organizar os treinos de hoje, dividir os atletas por objetivo, acompanhar a execução das sessões e identificar quem precisa de atenção.',
    suggestions: ['Quais grupos treinam hoje?', 'Quem está com baixa prontidão?', 'Mostre os treinos pendentes.'],
  },
  {
    role: 'prep',
    name: 'Preparador físico',
    assistantName: 'VUKZ Assist — Preparador físico',
    greeting: 'Olá, Eduarda. Posso ajudar você a analisar a carga dos atletas, organizar os grupos físicos do dia e identificar sinais de fadiga, recuperação ou risco de sobrecarga.',
    suggestions: ['Quem está com carga elevada?', 'Quais sessões físicas temos hoje?', 'Quem precisa de recuperação?'],
  },
  {
    role: 'nutritionist',
    name: 'Nutricionista',
    assistantName: 'VUKZ Assist — Nutricionista',
    greeting: 'Olá, Carla. Posso ajudar você a acompanhar planos alimentares, adesão, hidratação e alertas nutricionais dos atletas e categorias sob sua responsabilidade.',
    suggestions: ['Quais atletas estão sem plano?', 'Quem está com baixa adesão?', 'Mostre os alertas por categoria.'],
  },
  {
    role: 'manager',
    name: 'Gestor',
    assistantName: 'VUKZ Assist — Gestor',
    greeting: 'Olá, Carlos. Posso ajudar você a acompanhar a operação esportiva do clube, comparar categorias, visualizar o trabalho da equipe e identificar pontos que precisam de atenção.',
    suggestions: ['Como está a operação de hoje?', 'Quais categorias precisam de atenção?', 'Mostre os indicadores do clube.'],
  },
];

export const ASSIST_PROFILES_EN = [
  {
    role: 'athlete',
    name: 'Athlete',
    assistantName: 'VUKZ Assist — Athlete',
    greeting: 'Hi, Lucas. I am here to help you organize your sports day. I can explain your training, follow your well-being, check your schedule and help you understand your nutrition plan.',
    suggestions: ['What is my training today?', 'How is my recovery?', 'What is my next meal?'],
  },
  {
    role: 'coach',
    name: 'Coach',
    assistantName: 'VUKZ Assist — Coach',
    greeting: 'Hi, Ana. I can help you organize today\u2019s sessions, split athletes by goal, follow session execution and identify who needs attention.',
    suggestions: ['Which groups train today?', 'Who has low readiness?', 'Show pending sessions.'],
  },
  {
    role: 'prep',
    name: 'Physical trainer',
    assistantName: 'VUKZ Assist — Physical trainer',
    greeting: 'Hi, Eduarda. I can help you analyze athlete load, organize today\u2019s physical groups and spot signs of fatigue, recovery or overload risk.',
    suggestions: ['Who has high load?', 'Which physical sessions today?', 'Who needs recovery?'],
  },
  {
    role: 'nutritionist',
    name: 'Nutritionist',
    assistantName: 'VUKZ Assist — Nutritionist',
    greeting: 'Hi, Carla. I can help you track nutrition plans, adherence, hydration and nutrition alerts for the athletes and categories under your responsibility.',
    suggestions: ['Which athletes have no plan?', 'Who has low adherence?', 'Show alerts by category.'],
  },
  {
    role: 'manager',
    name: 'Manager',
    assistantName: 'VUKZ Assist — Manager',
    greeting: 'Hi, Carlos. I can help you follow the club\u2019s sports operation, compare categories, see the staff\u2019s work and identify points that need attention.',
    suggestions: ['How is today\u2019s operation?', 'Which categories need attention?', 'Show club indicators.'],
  },
];

export default AssistProfilesCarousel;
